import { Link } from "react-router-dom";
import {
  Database, Cube, ChartLineUp, Certificate, GridFour, Waveform,
  BookOpen, Code, Cpu, ShieldCheck, ArrowRight,
} from "@phosphor-icons/react";
import { Section, PageHero, EyebrowLabel, CalloutCard } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const SECTIONS = [
  {
    id: "getting-started",
    icon: BookOpen,
    title: "Getting started",
    body: [
      "Every SRES tenant ships with an empty workspace and a single superadmin account. The onboarding flow guides your team through creating the first company, inviting users, and importing the first drillhole database.",
      "Recommended path: create the workspace → invite two additional users (a geologist and a Competent Person) → import a sample dataset from the built-in library → run through the five-step pipeline (ingest → QAQC → statistics → variography → kriging → classification → CPR).",
    ],
    subsections: [
      { title: "System requirements", body: "Modern Chromium, Firefox or Safari. Screen size ≥ 1280 × 800 recommended. No desktop installs, no license servers, no dongles." },
      { title: "Account creation", body: "Superadmins invite users via the Company Admin panel. Each user receives an email with a one-time link that forces a password reset on first login." },
      { title: "Sample data", body: "Every new tenant includes the SRES sample project — a small Fe-ore drillhole database (12 holes, 400 assays) you can push through the full pipeline in under 30 minutes." },
    ],
  },
  {
    id: "data-ingest",
    icon: Database,
    title: "Data ingest",
    body: [
      "SRES accepts Collar, Survey, Assay and Geology CSVs plus QAQC control samples (CRM, Blank, Duplicate). Column names are auto-mapped from industry aliases — most files import on the first try.",
      "The ingest UI shows per-file row counts, a hole-count summary, and any validation issues categorised by severity (error / warning / info). Errors block downstream steps until resolved; warnings and infos are logged.",
    ],
    subsections: [
      { title: "Column auto-map", body: "Recognises BHID → hole_id, easting → x, northing → y, elevation → z, from/to intervals, grade columns, lithology codes and structural attributes." },
      { title: "Composite lengths", body: "SRES exposes composite length as an explicit parameter — 1 m, 2 m, 3 m or custom. Composites are stored per project, not per file, so they survive re-ingest." },
      { title: "Domain scripts", body: "Save reusable pandas queries per project and per user (lithology filters, grade cuts, spatial windows)." },
    ],
  },
  {
    id: "qaqc",
    icon: ShieldCheck,
    title: "QAQC & validation",
    body: [
      "Deep validation catches missing surveys, out-of-hole assays, geometry overlaps and negative depths before they contaminate a block model. QAQC analytics deliver control-chart envelopes on CRMs, blanks and duplicates.",
      "Every batch produces a validation report (PDF + JSON) and a QAQC report. Both are versioned per project so a reviewer can see how data quality evolved through the drilling programme.",
    ],
    subsections: [
      { title: "Validation categories", body: "Errors (block further work), warnings (recommend fix), infos (visibility only). Every issue lists affected hole IDs and one-click drill-down." },
      { title: "Precision & bias", body: "Half-Absolute Relative Difference (HARD), Sample Bias %, top-cut suggestion, log-probability plots and control-chart envelopes." },
    ],
  },
  {
    id: "estimation",
    icon: Cube,
    title: "Estimation engines",
    body: [
      "SRES ships three complementary interpolators: Inverse-Distance Weighting (IDW), Nearest Neighbour and Ordinary Kriging. All three support composite lengths, per-lithology density mapping and Measured / Indicated / Inferred classification.",
      "Kriging supports fitted spherical, exponential and Gaussian variogram models. Anisotropy is detected from the experimental variogram and confirmed by the user before the block model is committed.",
    ],
    subsections: [
      { title: "Ordinary Kriging", body: "Search neighbourhood, minimum samples, maximum samples, per-domain variogram fits, slope-of-regression for classification." },
      { title: "Block model outputs", body: "Grade, tonnage, classification and confidence per block. Exported as CSV, GeoJSON, SHP or DWG-ready formats." },
    ],
  },
  {
    id: "variography",
    icon: Waveform,
    title: "Variography",
    body: [
      "Experimental variograms are computed directly from your composites — no manual pair-list export. Fitted models are plotted alongside the experimental points so you (and your auditor) can eyeball the fit.",
      "Every fit records the nugget, sill, range and model type into the CPR narrative. There is nowhere for a bad assumption to hide.",
    ],
    subsections: [
      { title: "Model types", body: "Spherical, exponential, Gaussian. Each fit exposes nugget, sill and range as adjustable parameters." },
      { title: "Anisotropy", body: "Directional variograms and rotation angles supported per domain." },
    ],
  },
  {
    id: "classification",
    icon: GridFour,
    title: "Classification & confidence",
    body: [
      "SRES supports Measured / Indicated / Inferred classification based on search-neighbourhood coverage, number of informing samples and slope-of-regression thresholds.",
      "Every block is stamped with its own classification and a confidence score. The CPR narrative surfaces the aggregate classification breakdown per lithology.",
    ],
    subsections: [
      { title: "Classification rules", body: "Configurable per project. Defaults follow JORC / NI 43-101 guidance." },
      { title: "Confidence audit", body: "Every block links back to the samples that informed it. Auditors can drill down from tonnage to sample." },
    ],
  },
  {
    id: "economics",
    icon: ChartLineUp,
    title: "Economics & sensitivity",
    body: [
      "Floating-cone pit optimisation runs on the block model with commodity-aware pricing and slope constraints. Cut-off × NPV sweeps are visualised as a live slider — no re-runs required.",
      "Sensitivity sweeps span price, mining cost, processing cost and recovery. Monte-Carlo NPV supports triangular distributions with 1,000 – 10,000 trials.",
    ],
    subsections: [
      { title: "Pit optimisation", body: "Slope-constrained floating-cone. Outputs ore, waste, strip ratio and NPV per shell." },
      { title: "Monte-Carlo NPV", body: "Configurable trial count, triangular distributions and P10/P50/P90 disclosures." },
    ],
  },
  {
    id: "reporting",
    icon: Certificate,
    title: "Competent Person reports",
    body: [
      "SRES assembles a full CPR PDF (and DOCX) with cover, executive summary, methodology, findings, sensitivity and appendices. Signature blocks accept uploaded images for Prepared / Reviewed / Approved.",
      "Every export is snapshotted. Watermarked public share links carry expiry, view counter and audit log.",
    ],
    subsections: [
      { title: "Templates", body: "JORC, NI 43-101, SAMREC, CIM and PERC-aligned templates ship out of the box." },
      { title: "Public share", body: "Configurable expiry (7 / 30 / 90 days), IP allow-listing on request, per-token view count." },
    ],
  },
  {
    id: "api",
    icon: Code,
    title: "REST API",
    body: [
      "Every UI action in SRES is backed by a documented REST endpoint. Enterprise tenants can wire SRES directly into their reporting pipelines and BI tools.",
      "Full API reference is provided as an OpenAPI spec inside the workspace, plus curated code samples in Python and TypeScript.",
    ],
    subsections: [
      { title: "Authentication", body: "JWT bearer tokens plus optional API keys for machine-to-machine integrations." },
      { title: "Rate limits", body: "Per-tenant rate limits are configurable in the Enterprise plan." },
    ],
  },
  {
    id: "cli",
    icon: Cpu,
    title: "CLI & automation",
    body: [
      "Long-running kriging or pit optimisation jobs can be triggered from the CLI and orchestrated by CI systems. Every job returns a snapshot ID that can be re-downloaded via the API.",
      "Batch operations across projects (portfolio pit sweeps, bulk CPR regeneration) are supported for enterprise deployments.",
    ],
    subsections: [
      { title: "Job orchestration", body: "Async background jobs with progress feedback, cancellation and retries." },
      { title: "Batch operations", body: "Portfolio-level pit sweeps and CPR regeneration across dozens of projects." },
    ],
  },
];

export default function DocumentationPage() {
  useSEO({
    title: "Documentation · The SRES reference",
    description: "Full reference for the Shanvi Resource Estimation Studio — ingest, QAQC, estimation, variography, classification, pit economics and CPR reporting.",
    keywords: "SRES documentation, kriging reference, block model docs, JORC report guide",
    path: "/docs",
  });

  return (
    <div data-testid="documentation-page">
      <PageHero
        eyebrow="Documentation"
        title="The reference every SRES user should keep open."
        description="Ten reference sections covering the platform end-to-end — with tuning cheatsheets and code samples where relevant."
        testid="docs-hero"
      />

      <Section className="py-20 md:py-24" testid="docs-body">
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-10">
          <aside className="lg:sticky lg:top-28 self-start">
            <EyebrowLabel className="mb-5">Contents</EyebrowLabel>
            <ul className="space-y-2 text-[13px]">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-slate-600 hover:text-brand-blue transition-colors leading-relaxed inline-flex items-center gap-2"
                    data-testid={`docs-nav-${s.id}`}
                  >
                    <s.icon size={14} weight="bold" className="text-brand-blue/70" />
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className="min-w-0 space-y-16">
            {SECTIONS.map((s, i) => (
              <section id={s.id} key={s.id} className="scroll-mt-28" data-testid={`docs-section-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                    <s.icon size={20} weight="bold" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-mono">Section {String(i + 1).padStart(2, "0")}</div>
                </div>
                <h2 className="mt-4 font-heading font-black text-slate-900 text-3xl md:text-4xl tracking-tight leading-tight">{s.title}</h2>
                <div className="mt-4 space-y-4 text-slate-700 text-[15px] leading-relaxed max-w-3xl">
                  {s.body.map((para, pi) => (
                    <p key={pi}>{para}</p>
                  ))}
                </div>

                {s.subsections?.length ? (
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {s.subsections.map((sub) => (
                      <div key={sub.title} className="p-5 rounded-xl border border-slate-200 bg-white">
                        <div className="font-heading font-bold text-slate-900 text-[15px]">{sub.title}</div>
                        <p className="mt-2 text-slate-600 text-[13px] leading-relaxed">{sub.body}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ))}
          </div>
        </div>
      </Section>

      <Section className="py-16 md:py-24 bg-[#F7F8FB]" testid="docs-cta">
        <CalloutCard
          eyebrow="Need something not here?"
          title="Every SRES tenant ships with a named support geologist — ask them, don't fight the docs."
          description="If a workflow you need isn't in this reference, tell us. We update the docs from customer conversations, not from marketing."
          cta="Contact support"
          to="/support"
          testid="docs-cta-callout"
        />
      </Section>
    </div>
  );
}
